import { lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    AccountingConfigurationAdditionalExpensesAccountingCategories,
    AccountingConfigurationAlternativePaymentAccountingCategories,
    AccountingConfigurationExternalPaymentAccountingCategories,
    AccountingConfigurationGenericPaymentsAccountingCategories,
    AccountingConfigurationPaymentAccountingCategories,
} from '@mews-commander/data-access';
import { AccountingCategoryGroup } from './types';

export const getAdditionalExpensesFields = ({
    showAdditionalExpenses,
    showInvoiceFee,
    showSurcharge,
    showMulticurrencyFee,
}: {
    showAdditionalExpenses: boolean;
    showInvoiceFee: boolean;
    showSurcharge: boolean;
    showMulticurrencyFee: boolean;
}) =>
    ({
        additionalExpenseRebateAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeAdditionalExpenseRebate),
            show: showAdditionalExpenses,
        },
        cancellationFeeAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeCancellationFee),
        },
        cityTaxAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeCityTax),
        },
        cityTaxDiscountAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeCityTaxDiscount),
        },
        customItemAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeCustomItem),
        },
        depositAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeDeposit),
        },
        exchangeRateDifferenceAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeExchangeRateDifference),
        },
        nightRebateAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeNightRebate),
        },
        productOrderRebateAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeProductOrderRebate),
        },
        serviceChargeAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeServiceCharge),
        },
        taxCorrectionAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeTaxCorrection),
        },
        invoiceFeeAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeInvoiceFee),
            show: showInvoiceFee,
        },
        surchargeAccountingCategoryId: {
            label: lt(TextKey.OrderItemTypeSurcharge),
            show: showSurcharge,
        },
        multicurrencyFeeAccountingCategoryId: {
            label: lt(TextKey.MulticurrencyFee),
            show: showMulticurrencyFee,
        },
    }) satisfies AccountingCategoryGroup<AccountingConfigurationAdditionalExpensesAccountingCategories>;

export const getExternalPayments = () =>
    ({
        amexAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeAmex),
        },
        bacsAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBacs),
        },
        badDebtsAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBadDebts),
        },
        bancontactAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBancontact),
        },
        bankChargesAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBankCharges),
        },
        barterAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBarter),
        },
        cardCheckAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCardCheck),
        },
        cashAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCash),
        },
        chequeAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCheque),
        },
        chequeVacancesAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeChequeVacances),
        },
        commissionAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCommission),
        },
        complimentaryAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeComplimentary),
        },
        creditCardAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCreditCard),
        },
        crossSettlementAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCrossSettlement),
        },
        depositCashAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeDepositCash),
        },
        depositCheckAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeDepositCheck),
        },
        depositCreditCardAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeDepositCreditCard),
        },
        depositWireTransferAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeDepositWireTransfer),
        },
        dinersClubAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeDinersClub),
        },
        discoverAccountingCategoryId: {
            label: lt(TextKey.Discover),
        },
        exchangeRateDifferenceAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeExchangeRateDifference),
        },
        exchangeRoundingDifferenceAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeExchangeRoundingDifference),
        },
        giftCardAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeGiftCard),
        },
        idealAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeIDeal),
        },
        invoiceAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeInvoice),
        },
        jcbAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeJcb),
        },
        loyaltyCardAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeLoyaltyCard),
        },
        loyaltyPointsAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeLoyaltyPoints),
        },
        mastercardAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeMasterCard),
        },
        onlinePaymentAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeOnlinePayment),
        },
        paymentHubRedirectionAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypePaymentHubRedirection),
        },
        payPalAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypePayPal),
        },
        posDiningAndSpaRewardAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypePosDiningAndSpaReward),
        },
        prepaymentAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypePrepayment),
        },
        rekaAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeReka),
        },
        resellerAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeReseller),
        },
        twintAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeTwint),
        },
        unionPayAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeUnionPay),
        },
        unspecifiedAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeUnspecified),
        },
        visaAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeVisa),
        },
        voucherAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeVoucher),
        },
        wireTransferAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypesWireTransfer),
        },
        directDebitAccountingCategoryId: {
            label: lt(TextKey.PaymentMethodDirectDebit),
        },
    }) satisfies AccountingCategoryGroup<AccountingConfigurationExternalPaymentAccountingCategories>;

export const getPaymentMethods = () =>
    ({
        amexAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeAmex),
        },
        bancomatAccountingCategoryId: {
            label: lt(TextKey.Bancomat),
        },
        bancontactAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBancontact),
        },
        bcAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeBc),
        },
        carteBleueAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeCarteBleue),
        },
        dankortAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeDankort),
        },
        dinersClubAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeDinersClub),
        },
        discoverAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeDiscover),
        },
        eftposAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeEtfpos),
        },
        epsAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeEps),
        },
        giroAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeGiro),
        },
        interacAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeInterac),
        },
        isracardAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeIsracard),
        },
        jcbAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeJcb),
        },
        maestroAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeMaestro),
        },
        mastercardAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeMasterCard),
        },
        mepsAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeMeps),
        },
        mirAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeMir),
        },
        netsAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeNets),
        },
        postFinanceAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypePostFinance),
        },
        ruPayAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeRuPay),
        },
        troyAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeTroy),
        },
        unionPayAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeUnionPay),
        },
        vpayAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeVPay),
        },
        verveAccountingCategoryId: {
            label: lt(TextKey.ExternalPaymentTypeVerve),
        },
        visaAccountingCategoryId: {
            label: lt(TextKey.CreditCardTypeVisa),
        },
    }) satisfies AccountingCategoryGroup<AccountingConfigurationPaymentAccountingCategories>;

export const getPaymentFields = ({ showTaxDeductedPayment }: { showTaxDeductedPayment: boolean }) =>
    ({
        cashPaymentAccountingCategoryId: {
            label: lt(TextKey.CashPayment),
        },
        invoicePaymentAccountingCategoryId: {
            label: lt(TextKey.InvoicePayment),
        },
        taxDeductedPaymentAccountingCategoryId: {
            label: lt(TextKey.TaxDeductedPayment),
            show: showTaxDeductedPayment,
        },
        unspecifiedCreditCardPaymentAccountingCategoryId: {
            label: lt(TextKey.UnspecifiedCreditCardPayment),
        },
    }) satisfies AccountingCategoryGroup<AccountingConfigurationGenericPaymentsAccountingCategories>;

export const getAlternativePayments = () =>
    ({
        idealAccountingCategoryId: {
            label: lt(TextKey.IDeal),
        },
        sepaDirectDebitAccountingCategoryId: {
            label: lt(TextKey.SEPADirectDebit),
        },
    }) satisfies AccountingCategoryGroup<AccountingConfigurationAlternativePaymentAccountingCategories>;
